import './MembershipTypeListTable.css';

import type { MembershipAnalytics, MembershipTypeRow } from '../api/membership.api';
import Skeleton from '../../../shared/components/ui/Skeleton';
import { resolveThemeToken } from '../themeTokens';

type MembershipTypeListTableProps = {
  rows: MembershipTypeRow[];
  analytics?: MembershipAnalytics;
  loading?: boolean;
  onView: (row: MembershipTypeRow) => void;
  onEdit: (row: MembershipTypeRow) => void;
  onToggleActive?: (row: MembershipTypeRow) => void;
  onCreateNew?: () => void;
};

const formatRupeesFromPaise = (value: number): string => `₹${(Math.max(value || 0, 0) / 100).toLocaleString('en-IN')}`;
const formatCompactRupeesFromPaise = (value: number): string => {
  const rupees = Math.max(value || 0, 0) / 100;
  if (rupees >= 100000) return `₹${(rupees / 100000).toFixed(1).replace(/\.0$/, '')}L`;
  if (rupees >= 1000) return `₹${(rupees / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `₹${Math.round(rupees).toLocaleString('en-IN')}`;
};
const deriveValidityLabel = (days?: number): string => {
  if (!days) return '—';
  if (days >= 36500) return 'Lifetime';
  if (days >= 365 && days % 365 === 0) return `${days / 365} yr${days / 365 !== 1 ? 's' : ''}`;
  if (days >= 30 && days % 30 === 0) return `${days / 30} mo`;
  return `${days} days`;
};

const formatPointsMultiplier = (value?: number): string => {
  const safeValue = Number(value || 0);
  if (!safeValue) return '1×';
  const multiplier = 100 / safeValue;
  const rounded = Number.isInteger(multiplier) ? String(multiplier) : multiplier.toFixed(1).replace(/\.0$/, '');
  return `${rounded}×`;
};

const MembershipTypeListTable = ({
  rows,
  analytics,
  loading,
  onView,
  onEdit,
  onToggleActive,
  onCreateNew
}: MembershipTypeListTableProps) => {
  const membersByTier = new Map((analytics?.byTier || []).map((row) => [row.membershipTypeUuid, row.activeCount]));
  const revenueByTier = new Map((analytics?.byTier || []).map((row) => [row.membershipTypeUuid, row.revenuePaise]));

  if (loading) {
    return (
      <section className="membership-tier-grid" aria-label="Loading membership tiers">
        {Array.from({ length: 4 }).map((_, i) => (
          <article className="appointments-v2-card membership-tier-card membership-tier-card--loading" key={`types-row-${i}`} style={{ ['--card-index' as string]: i }}>
            <div className="appointments-v2-card-body membership-tier-card__body">
              <Skeleton height={18} width="40%" />
              <Skeleton height={30} width="75%" />
              <Skeleton height={16} width="60%" />
              <Skeleton height={90} width="100%" />
              <Skeleton height={18} width="50%" />
            </div>
          </article>
        ))}
      </section>
    );
  }

  if (!rows.length) {
    return (
      <section className="membership-tier-grid">
        <article className="appointments-v2-card membership-tier-card membership-tier-card--empty">
          <div className="appointments-v2-card-body membership-tier-card__body" style={{ justifyItems: 'center', alignContent: 'center', textAlign: 'center' }}>
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
              <rect x="8" y="16" width="48" height="32" rx="6" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" opacity="0.3" />
              <circle cx="32" cy="32" r="8" stroke="var(--color-accent)" strokeWidth="2" opacity="0.5" />
              <path d="M32 28v8M28 32h8" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>No membership tiers yet</h3>
            <p className="appointments-v2-empty-copy">Create the first one to start shaping your loyalty catalogue.</p>
            <button type="button" className="btn primary" onClick={onCreateNew}>Create your first tier</button>
          </div>
        </article>
      </section>
    );
  }

  return (
    <section className="membership-tier-grid" aria-label="Membership tiers">
      {rows.map((row, index) => {
        const theme = resolveThemeToken(row.themeColorToken);
        const members = membersByTier.get(row.membershipTypeUuid) || 0;
        const toneClass = theme.textMode === 'light' ? 'membership-tier-card--light' : 'membership-tier-card--dark';

        return (
          <article
            key={row.membershipTypeUuid}
            className={`appointments-v2-card membership-tier-card ${toneClass}`}
            style={{
              background: theme.cardBackground,
              ['--membership-tier-accent' as string]: theme.swatchHex,
              ['--membership-tier-ring' as string]: theme.accentRing,
              ['--card-index' as string]: index,
            }}
          >
            <div className="appointments-v2-card-body membership-tier-card__body">
              <div className="membership-tier-card__header">
                <span className="membership-tier-card__badge">{`TIER ${row.tierLevel || 1}`}</span>
                <span className={`membership-tier-card__status-dot ${row.isActive ? 'is-active' : 'is-inactive'}`} title={row.isActive ? 'Active' : 'Inactive'} />
              </div>
              <div className="membership-tier-card__identity">
                <h3>{row.name}</h3>
                <p>{row.description || 'Membership description will appear here once configured.'}</p>
                <p className="membership-tier-card__pricing">
                  {row.pricePaise === 0 ? 'Free' : formatRupeesFromPaise(row.pricePaise)}
                  {' · '}
                  {deriveValidityLabel(row.validityDays)}
                </p>
              </div>
              <div className="membership-tier-card__metrics">
                <div>
                  <span className="membership-tier-card__metricLabel">MEMBERS</span>
                  <strong>{members}</strong>
                </div>
                <div>
                  <span className="membership-tier-card__metricLabel">REV/30D</span>
                  <strong>{formatCompactRupeesFromPaise(revenueByTier.get(row.membershipTypeUuid) || 0)}</strong>
                </div>
                <div>
                  <span className="membership-tier-card__metricLabel">CHURN</span>
                  <strong>—</strong>
                </div>
                <div>
                  <span className="membership-tier-card__metricLabel">PTS MULT</span>
                  <strong>{row.pointsEnabled ? formatPointsMultiplier(row.pointsEarnRatePaisePerPoint) : '1×'}</strong>
                </div>
              </div>
              <div className="membership-tier-card__actions">
                <button type="button" className="btn primary membership-tier-card__viewBtn" onClick={() => onView(row)}>
                  Manage
                </button>
                <div className="membership-tier-card__secondary-actions">
                  <button type="button" className="btn ghost" onClick={() => onEdit(row)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className={`btn secondary ${row.isActive ? 'is-tier-active' : 'is-tier-inactive'}`}
                    onClick={() => onToggleActive?.(row)}
                  >
                    {row.isActive ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>
            </div>
          </article>
        );
      })}
      <button type="button" className="appointments-v2-card membership-tier-card membership-tier-card--new" onClick={onCreateNew} style={{ ['--card-index' as string]: rows.length }}>
        <div className="appointments-v2-card-body membership-tier-card__body">
          <span className="membership-tier-card__newBadge">+</span>
          <h3>New membership</h3>
          <p>Add another tier to your loyalty ladder with its own rules and rewards.</p>
        </div>
      </button>
    </section>
  );
};

export default MembershipTypeListTable;
